import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "./Analytics.css";
import { UserData } from "../model/user";
import { useEffect, useState } from "react";
import { Scout, ScoutStatus } from "../organisation/model/scout";
import { CenteredRow } from "../layout/Row";
import BorderButton from "../button/BorderButton";
import { SmallLogoWithDashboard } from "../logo/SmallLogo";
import LogoutWidget from "../logout/LogoutWidget";
import NotificationOverlay from "../status/NotificationOverlay";
import { queryScouts } from "../organisation/firestore/scouts";
import { ColumnCentered } from "../layout/Column";
import { connect } from "react-redux";
import { BatteryData, getBatteryStatus } from "../services/adminService";
import BatteryStatusChart from "../plot/BatteryStatusChart";
import LoadingAnimation from "../animation/LoadingAnimation";



enum BatteryTimeRange {
    two_weeks = 24 * 14,
    one_week = 24 * 7,
    three_days = 24 * 3,
    one_day = 24
}


const BatteryAnalyticsPage = (props: Props) => {
    //@ts-ignore
    const deviceId: string | undefined = props.match.params.deviceId;
    //@ts-ignore
    const organisationId: string | undefined = props.match.params.organisationId;

    const [timeRange, setTimeRange] = useState<BatteryTimeRange>(BatteryTimeRange.three_days);
    const [fromDate, setFromDate] = useState<Date>(getFromDate(BatteryTimeRange.three_days, new Date()));
    const [toDate, setToDate] = useState<Date>(new Date());
    return (
        <div className="Dashboard">
            <div className={"Dashboard-header"}>
                <SmallLogoWithDashboard fontSizeRem={1.625} />
                <LogoutWidget {...props} />
            </div>
            <NotificationOverlay />
            <CenteredRow style={{ paddingBottom: "2.5rem" }}>
                <h1>Battery analytics</h1>
            </CenteredRow>
            <TimeRangeSelection setTimeRange={setTimeRange} timeRange={timeRange} setFromDate={setFromDate} toDate={toDate} />
            <CenteredRow>
                <PickDate setToDate={setToDate} setFromDate={setFromDate} timeRange={timeRange} />
            </CenteredRow>
            <main className={"Graph-content"} style={{ maxWidth: "100vw", paddingLeft: "100" }}>
                {deviceId !== undefined ? (
                    <DeviceBatteryAnalyticsPage
                        id={deviceId}
                        timeRange={timeRange}
                        fromDate={fromDate}
                        toDate={toDate}
                        {...props}
                    />
                ) : organisationId !== undefined ? (
                    <OrganisationBatteryAnalyticsPage
                        id={organisationId}
                        timeRange={timeRange}
                        fromDate={fromDate}
                        toDate={toDate}
                        {...props}
                    />
                ) : (
                    <div>Missing device or organisation id</div>
                )}
            </main>
        </div>
    )
}

const OrganisationBatteryAnalyticsPage = (props: BatteryAnalyticsProps) => {
    const organisationId = props.id;
    const [scouts, setScouts] = useState<Scout[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    useEffect(() => {
        if (props.jwtToken) {
            queryScouts(organisationId, (s) => {
                s.sort((a, b) => a.status.displayName.localeCompare(b.status.displayName));
                setScouts(s);
            });
        }
    }, []);
    return (
        <>
            {scouts.map((scout) => (
                <DeviceBatteryAnalyticsPage {...props} id={scout.deviceId} scout={scout.status} />
            ))}
        </>
    );
};

const DeviceBatteryAnalyticsPage = (props: BatteryAnalyticsProps) => {
    const deviceId = props.id;
    const fromDate = props.fromDate
    const toDate = props.toDate;
    const [measurements, setMeasurements] = useState<BatteryData>();
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        if (props.jwtToken) {
            console.log("Getting battery analytics for device", deviceId);
            console.log("From date:", fromDate, "to date:", toDate);
            setLoading(true);
            getBatteryStatus(props.jwtToken, deviceId, fromDate, toDate)
                .then((data) => {
                    if (data.points.length === 0) {
                        console.log("No battery measurements found for device", deviceId, "in the specified time range.");
                        setMeasurements(undefined);
                    } else {
                        setMeasurements(data);
                    }
                    setLoading(false);
                })
                .catch((error) => {
                    console.error("Error fetching battery measurements:", error);
                    setLoading(false);
                });
        }


    }, [props.jwtToken, props.fromDate]);
    if (measurements === undefined) {
        return <div style={{marginBottom: "70px"}} >{loading && <CenteredRow> <LoadingAnimation height={40} width={40} color="blue"/></CenteredRow>}</div>
    }
    return (
        <>
            <ColumnCentered>
                <h2>Device {props.scout?.displayName ?? deviceId} • {props.scout?.deviceInfo.model ?? "NA"} • SDK: {props.scout?.deviceInfo.sdk ?? "NA"} • {props.scout?.appVersion ?? "NA"}
                    {loading && <span> <LoadingAnimation height={18} width={18} color="blue"/></span>}
                </h2>
                <BatteryStatusChart graphData={measurements} />
            </ColumnCentered>
        </>
    )

}

function TimeRangeSelection({ setTimeRange, timeRange, setFromDate, toDate }: { setTimeRange: (BatteryTimeRange) => void, timeRange: BatteryTimeRange; setFromDate: (date: Date) => void; toDate: Date }) {
    return (
        <CenteredRow style={{ paddingBottom: "2.5rem" }}>
            <BorderButton
                key={0}
                text="2 weeks"
                onClick={() => {
                    setFromDate(getFromDate(BatteryTimeRange.two_weeks, toDate))
                    setTimeRange(BatteryTimeRange.two_weeks);
                }}
                selected={timeRange === BatteryTimeRange.two_weeks}
            />
            <BorderButton
                key={1}
                text="1 week"
                onClick={() => {
                    setFromDate(getFromDate(BatteryTimeRange.one_week, toDate))
                    setTimeRange(BatteryTimeRange.one_week);
                }}
                selected={timeRange === BatteryTimeRange.one_week}
            />
            <BorderButton
                key={2}
                text="3 days"
                onClick={() => {
                    setFromDate(getFromDate(BatteryTimeRange.three_days, toDate))
                    setTimeRange(BatteryTimeRange.three_days);
                }}
                selected={timeRange === BatteryTimeRange.three_days}
            />
            <BorderButton
                key={3}
                text="1 day"
                onClick={() => {
                    setFromDate(getFromDate(BatteryTimeRange.one_day, toDate))
                    setTimeRange(BatteryTimeRange.one_day);
                }}
                selected={timeRange === BatteryTimeRange.one_day}
            />
        </CenteredRow>
    );
}

function PickDate({ setToDate, setFromDate, timeRange }: { setToDate: (date: Date) => void, setFromDate: (date: Date) => void, timeRange: BatteryTimeRange }) {
    const [selectedDate, setSelectedDate] = useState(
        new Date().toISOString().substring(0, 10)
    );
    return (
        <CenteredRow style={{ paddingBottom: "2.5rem" }}>
            <input
                type="date"
                value={selectedDate}
                onChange={(e) => {
                    const date = new Date(e.target.value);
                    setSelectedDate(e.target.value);
                    date.setHours(23, 59, 59, 999);
                    console.log("Selected date:", date);
                    setToDate(date);
                    setFromDate(getFromDate(timeRange, date));
                }}
            >
            </input>
        </CenteredRow>
    )
};

const mapStateToProps = (state: any): StateToProps => ({
    jwtToken: state.login.jwtToken,
    user: state.login.user,
    userData: state.login.userData,
});

const mapDispatchToProps: DispatchFromProps = {
    setUser: (_: firebase.User | undefined) => { },
    setUserData: (_: UserData | undefined) => { },
};

export default connect<StateToProps, DispatchFromProps>(
    // @ts-ignore
    mapStateToProps,
    mapDispatchToProps
)(BatteryAnalyticsPage);

interface StateToProps {
    jwtToken: string | undefined;
    user: firebase.User | undefined;
    userData: UserData | undefined;
}

interface DispatchFromProps {
    setUser: (_: firebase.User | undefined) => void;
    setUserData: (_: UserData | undefined) => void;
}

interface Props extends StateToProps, DispatchFromProps { }

interface BatteryAnalyticsProps extends Props {
    id: string;
    timeRange: BatteryTimeRange;
    fromDate: Date;
    toDate: Date;
    scout?: ScoutStatus;
}

function getFromDate(timeRange: BatteryTimeRange, toDate: Date): Date {
    return new Date(toDate.getTime() - timeRange * 60 * 60 * 1000);
}

